<script setup lang="ts">
import { computed } from 'vue'
import ListRow from './ListRow.vue'
import EntityIcon from './EntityIcon.vue'
import { Palette, paletteFromColour, type PaletteColour, type PaletteHue } from '../palette'
import { subjectIcon } from '../subjectIcons'

/**
 * SchemeListRow — planning scheme entry in a list. Mirrors Android's
 * HomeSchemeRow (PaletteIconTile + subject icon + unit/lesson count).
 *
 * Title format follows Android: `"<year>  ·  <subject>"` with the year
 * in medium weight and the subject in regular. Falls back to the scheme
 * name when neither is set.
 */

const props = defineProps<{
  name: string
  /** PaletteColour enum string from the API. */
  colour?: string | null
  hue?: PaletteHue
  /** Subject icon id (e.g. "Calculator"). Maps via subjectIcon(). */
  iconId?: string | null
  /** Optional metadata that swaps `name` for "year · subject" title. */
  teachingYearName?: string | null
  subjectName?: string | null
  unitCount?: number | null
  lessonPlanCount?: number | null
  to?: string | object
  showTopDivider?: boolean
}>()

const resolvedHue = computed<PaletteHue>(() => {
  if (props.hue) return props.hue
  if (props.colour) return paletteFromColour(props.colour as PaletteColour)
  return Palette.grey
})

const icon = computed(() => subjectIcon(props.iconId))

const hasSplitTitle = computed(() =>
  !!(props.teachingYearName?.trim() || props.subjectName?.trim()),
)

const metaLabel = computed(() => {
  const units = props.unitCount ?? 0
  const unitWord = units === 1 ? 'unit' : 'units'
  const lessons = props.lessonPlanCount ?? 0
  if (lessons === 0) return `${units} ${unitWord}`
  const lessonWord = lessons === 1 ? 'lesson plan' : 'lesson plans'
  return `${units} ${unitWord} · ${lessons} ${lessonWord}`
})
</script>

<template>
  <ListRow :to="to" :show-top-divider="showTopDivider">
    <template #leading>
      <EntityIcon :icon="icon" :palette="resolvedHue" :size="44" :corner-radius="14" />
    </template>

    <template #title>
      <template v-if="hasSplitTitle">
        <span v-if="teachingYearName" class="truncate">{{ teachingYearName }}</span>
        <span v-if="teachingYearName && subjectName" aria-hidden="true" class="title-bullet">·</span>
        <span v-if="subjectName" class="list-row-title-secondary truncate">{{ subjectName }}</span>
      </template>
      <span v-else class="truncate">{{ name }}</span>
    </template>

    <template #meta>
      <span class="tnum">{{ metaLabel }}</span>
    </template>
  </ListRow>
</template>

<style scoped>
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title-bullet {
  color: var(--ink-400);
}
</style>
